<template>
	<HorizontalSplit>
		<template #top>
			<div class="map-wrap">
				<mgl-map
					:map-style="mapStyleURL(ALIDADE_SMOOTH)"
					:center="initialCenter"
					:zoom="11"
					@map:load="onMapLoad"
				>
					<mgl-geo-json-source
						v-if="featureLocation"
						source-id="inspection-feature-point"
						:data="featurePointData"
					>
						<mgl-circle-layer
							layer-id="inspection-feature-point-layer"
							:paint="{
								'circle-color': '#22c55e',
								'circle-opacity': 0.6,
								'circle-radius': 8,
								'circle-stroke-color': '#22c55e',
								'circle-stroke-width': 2,
							}"
						/>
					</mgl-geo-json-source>
				</mgl-map>
				<div v-if="missingFeatureLocation" class="map-note">
					No location recorded for this feature.
				</div>
			</div>
		</template>

		<template #bottom>
			<div class="container-fluid py-4">
				<div class="row mb-3">
					<div class="col">
						<RouterLink
							to="/_/review/inspection"
							class="btn btn-outline-secondary btn-sm"
						>
							<i class="bi bi-arrow-left"></i> All inspections
						</RouterLink>
					</div>
				</div>

				<div class="row mb-3">
					<div class="col">
						<h1 class="h2">
							Inspection #{{ inspection ? inspection.id : id }}
						</h1>
					</div>
				</div>

				<div v-if="loading" class="text-center py-5">
					<div class="spinner-border text-primary" role="status"></div>
					<p class="mt-2 text-muted">Loading inspection…</p>
				</div>

				<div v-else-if="!inspection" class="text-center py-4 text-muted">
					Inspection not found.
				</div>

				<div v-else class="card">
					<div class="card-header">Inspection Info</div>
					<div class="card-body">
						<dl class="row mb-0">
							<dt class="col-sm-4 text-muted">Inspection ID</dt>
							<dd class="col-sm-8">
								<code>{{ inspection.id }}</code>
							</dd>

							<dt class="col-sm-4 text-muted">Organization ID</dt>
							<dd class="col-sm-8">
								<code>{{ inspection.organization_id }}</code>
							</dd>

							<dt class="col-sm-4 text-muted">Feature ID</dt>
							<dd class="col-sm-8">
								<code>{{ inspection.feature_id }}</code>
							</dd>

							<dt class="col-sm-4 text-muted">Creator</dt>
							<dd class="col-sm-8">{{ creatorText }}</dd>

							<dt class="col-sm-4 text-muted">Method</dt>
							<dd class="col-sm-8">{{ inspection.method || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Start Time</dt>
							<dd class="col-sm-8">
								<LocalizedTime :datetime="inspection.start_datetime" />
							</dd>

							<dt class="col-sm-4 text-muted">End Time</dt>
							<dd class="col-sm-8">
								<LocalizedTime
									v-if="inspection.end_datetime"
									:datetime="inspection.end_datetime"
								/>
								<span v-else>—</span>
							</dd>

							<dt class="col-sm-4 text-muted">Duration</dt>
							<dd class="col-sm-8">
								{{
									formatDuration(
										durationSeconds(
											inspection.start_datetime,
											inspection.end_datetime,
										),
									)
								}}
							</dd>

							<dt class="col-sm-4 text-muted">Production</dt>
							<dd class="col-sm-8">{{ productionText }}</dd>

							<dt class="col-sm-4 text-muted">Stage</dt>
							<dd class="col-sm-8">{{ inspection.stage || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Density</dt>
							<dd class="col-sm-8">{{ inspection.density || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Created</dt>
							<dd class="col-sm-8">
								<LocalizedTime :datetime="inspection.created" />
							</dd>
						</dl>
					</div>
				</div>

				<div v-if="inspection" class="row mt-3">
					<div class="col-lg-6 mb-3">
						<div class="card h-100">
							<div
								class="card-header d-flex justify-content-between align-items-center"
							>
								<span>Feature</span>
								<RouterLink
									:to="{
										name: 'Feature Review',
										params: { id: inspection.feature_id.toString() },
									}"
									class="btn btn-sm btn-outline-primary"
								>
									<i class="bi bi-box-arrow-up-right"></i> View feature
								</RouterLink>
							</div>
							<div class="card-body">
								<dl v-if="feature" class="row mb-0">
									<dt class="col-sm-4 text-muted">Feature ID</dt>
									<dd class="col-sm-8">
										<code>{{ feature.id }}</code>
									</dd>

									<dt class="col-sm-4 text-muted">Type</dt>
									<dd class="col-sm-8">
										<span class="badge bg-primary">{{ feature.type }}</span>
									</dd>

									<dt class="col-sm-4 text-muted">Condition</dt>
									<dd class="col-sm-8">
										<span v-if="feature.pool" class="badge bg-secondary">
											{{ feature.pool.current_condition }}
										</span>
										<span v-else class="text-muted">No pool</span>
									</dd>

									<dt class="col-sm-4 text-muted">Location</dt>
									<dd class="col-sm-8">{{ featureLocationText }}</dd>
								</dl>

								<div v-else-if="featureFailed" class="text-muted">
									Could not load feature #{{ inspection.feature_id }}.
								</div>

								<div v-else class="text-muted">
									<span
										class="spinner-border spinner-border-sm me-2"
										role="status"
									></span>
									Loading feature…
								</div>
							</div>
						</div>
					</div>

					<div class="col-lg-6 mb-3">
						<div class="card h-100">
							<div class="card-header">Site</div>
							<div class="card-body">
								<template v-if="site">
									<dl class="row mb-0">
										<dt class="col-sm-4 text-muted">Site ID</dt>
										<dd class="col-sm-8">
											<code>{{ site.id }}</code>
										</dd>

										<dt class="col-sm-4 text-muted">Address</dt>
										<dd class="col-sm-8">{{ siteAddressText }}</dd>

										<dt class="col-sm-4 text-muted">Tags</dt>
										<dd class="col-sm-8">
											<template v-if="siteTags.length">
												<span
													v-for="[key, value] in siteTags"
													:key="key"
													class="badge bg-light text-dark me-1"
												>
													{{ value ? `${key}: ${value}` : key }}
												</span>
											</template>
											<span v-else class="text-muted">—</span>
										</dd>

										<dt class="col-sm-4 text-muted">Notes</dt>
										<dd class="col-sm-8">
											<span v-if="site.notes" class="notes-clamp">
												{{ site.notes }}
											</span>
											<span v-else class="text-muted">—</span>
										</dd>
									</dl>

									<div class="mt-3">
										<RouterLink
											:to="routes.SiteDetail(site.id)"
											class="btn btn-sm btn-outline-primary"
										>
											<i class="bi bi-box-arrow-up-right"></i>
											View site details
										</RouterLink>
									</div>
								</template>

								<div v-else-if="siteMessage" class="text-muted">
									{{ siteMessage }}
								</div>

								<div v-else class="text-muted">
									<span
										class="spinner-border spinner-border-sm me-2"
										role="status"
									></span>
									Loading site…
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</HorizontalSplit>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<style scoped>
.map-wrap {
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
}

.map-note {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: rgba(255, 255, 255, 0.9);
	border-radius: 6px;
	padding: 8px 14px;
	color: #6c757d;
	font-size: 0.875rem;
	pointer-events: none;
}

/* Long free-text notes must not stretch the site card past the row height. */
.notes-clamp {
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 4;
	line-clamp: 4;
	overflow: hidden;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
	MglCircleLayer,
	MglGeoJsonSource,
	MglMap,
	type MglEvent,
	useMap,
} from "vue-maplibre-gl";
import type { GeoJSON } from "geojson";

import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import LocalizedTime from "@/components/LocalizedTime.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { durationSeconds, formatAddress, formatDuration } from "@/format";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { mapStyleURL } from "@/map/util";
import { useRoutes } from "@/route/use";
import { useFeatureStore } from "@/store/feature";
import { useStoreInspection } from "@/store/inspection";
import { useSessionStore } from "@/store/session";
import { useStoreSite } from "@/store/site";
import type { FeatureDetail, Inspection, Site } from "@/type/api";

interface Props {
	id: string;
}

// The inspection carries no geometry: its location is the linked feature's
// point, so the map always flies to a single point rather than fitting bounds.
const POINT_ZOOM = 16;

const props = defineProps<Props>();
const store = useStoreInspection();
const featureStore = useFeatureStore();
const siteStore = useStoreSite();
const session = useSessionStore();
const routes = useRoutes();
const mapInstance = useMap();

const inspection = ref<Inspection | null>(null);
const feature = ref<FeatureDetail | null>(null);
const featureFailed = ref(false);
const site = ref<Site | null>(null);
const siteMessage = ref("");
const loading = ref(true);
const mapReady = ref(false);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

onMounted(async () => {
	try {
		inspection.value = await store.fetchByID(parseInt(props.id, 10));
	} catch (err) {
		toastTitle.value = "Failed to load inspection";
		toastMessage.value =
			err instanceof Error ? err.message : "Failed to load inspection.";
		toastShow.value = true;
	} finally {
		loading.value = false;
	}

	if (inspection.value) {
		await loadFeatureAndSite(inspection.value.feature_id);
	}
});

// The feature and its site are the location context for the inspection. A
// failed lookup degrades the affected card to a muted note instead of blocking
// the page or throwing away the inspection that did load.
async function loadFeatureAndSite(featureId: number): Promise<void> {
	let featureDetail: FeatureDetail;
	try {
		featureDetail = await featureStore.fetchByID(featureId);
	} catch {
		featureFailed.value = true;
		siteMessage.value = "Site unknown — the feature could not be loaded.";
		return;
	}
	feature.value = featureDetail;

	if (!featureDetail.site_id) {
		siteMessage.value = "This feature is not linked to a site.";
		return;
	}

	try {
		site.value = await siteStore.fetchOne(featureDetail.site_id);
	} catch {
		siteMessage.value = "Could not load site details.";
	}
}

// Start centered on the district's service area when available.
const initialCenter = computed<[number, number]>(() => {
	const sa = session.organization?.service_area;
	if (sa && sa.min) {
		return [
			(sa.min.longitude + sa.max.longitude) / 2,
			(sa.min.latitude + sa.max.latitude) / 2,
		];
	}
	return [-119.175, 36.24];
});

// The linked feature's location, when it actually carries coordinates.
const featureLocation = computed<{
	latitude: number;
	longitude: number;
} | null>(() => {
	const loc = feature.value?.location;
	if (!loc || loc.latitude == null || loc.longitude == null) return null;
	return { latitude: loc.latitude, longitude: loc.longitude };
});

// A single-point FeatureCollection for the map overlay.
const featurePointData = computed<GeoJSON.FeatureCollection>(() => {
	const loc = featureLocation.value;
	if (!loc) return { type: "FeatureCollection", features: [] };
	return {
		type: "FeatureCollection",
		features: [
			{
				type: "Feature",
				properties: {},
				geometry: {
					type: "Point",
					coordinates: [loc.longitude, loc.latitude],
				},
			},
		],
	};
});

// True once we know the feature has no usable location (loaded without one, or
// failed to load at all) so the map can explain why it is not centred.
const missingFeatureLocation = computed<boolean>(
	() =>
		!!inspection.value &&
		(featureFailed.value || (!!feature.value && !featureLocation.value)),
);

// The inspection has no geometry of its own; the linked feature's point is the
// only thing to fly to.
function focusFeatureLocation(): void {
	const map = mapInstance.map;
	const loc = featureLocation.value;
	if (!mapReady.value || !map || !loc) return;

	map.flyTo({
		center: [loc.longitude, loc.latitude],
		zoom: POINT_ZOOM,
		duration: 800,
	});
}

// The map's load event and the feature fetch race each other, so centering is
// attempted from both the load handler and the location watcher; whichever
// arrives last succeeds.
function onMapLoad(_event: MglEvent<"load">): void {
	mapReady.value = true;
	focusFeatureLocation();
}

watch(
	() => feature.value?.location,
	() => focusFeatureLocation(),
);

const creatorText = computed<string>(() => {
	const i = inspection.value;
	if (!i || i.creator_id === null) return "—";
	return `#${i.creator_id}`;
});

const productionText = computed<string>(() => {
	return inspection.value?.production ? "Yes" : "No";
});

const featureLocationText = computed<string>(() => {
	const loc = featureLocation.value;
	if (!loc) return "—";
	return `${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}`;
});

const siteAddressText = computed<string>(() => {
	const address = site.value?.address;
	return address ? formatAddress(address) : "—";
});

// Site tags arrive as a plain object over JSON but are typed as a Map, so
// tolerate both (same handling as SiteDetailPanelSingle).
const siteTags = computed<[string, string][]>(() => {
	const tags: unknown = site.value?.tags;
	if (!tags) return [];
	if (tags instanceof Map) return [...tags.entries()];
	if (typeof tags === "object") {
		return Object.entries(tags as Record<string, string>);
	}
	return [];
});
</script>
